import { Alert, Group, Loader, NumberInput, Select, SimpleGrid, Stack, Textarea, TextInput } from '@mantine/core';
import { useForm } from '@mantine/form';
import { notifications } from '@mantine/notifications';
import { IconCheck, IconPlus, IconTrashX } from '@tabler/icons-react';
import { useApi } from 'api/api-context';
import ActionIcon from 'components/ActionIcon';
import Button from 'components/Button';
import FormInputGroup from 'components/forms/FormInputGroup';
import FormWrapper from 'components/forms/FormWrapper';
import OrganizationStructureScope from 'components/forms/OrganizationStructureScope';
import { DeviceTypeSelect } from 'components/selects/DeviceTypeSelect';
import { useDirtyFormHandler } from 'hooks/use-dirty-form-handler';
import { useResponsiveDesign } from 'hooks/use-responsive-design';
import DashboardLayout from 'layouts/dashboard/DashboardLayout';
import { ROLE_ADMIN_ID } from 'model/Role';
import { useEffect, useState } from 'react';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import { DASHBOARD_PAGE_PATH, HR_TERM_TYPES_PAGE_PATH } from 'routes/paths';
import { SUCCESS_NOTIFICATION_COLOR } from 'utils/constants';

/** Dedicated create/edit page for one HR term type. */
export default function HrTermTypeForm() {
  const { getAction, roleId } = useApi();
  const { isTablet } = useResponsiveDesign();
  const navigate = useNavigate();
  const { termTypeId } = useParams();
  const editing = termTypeId !== undefined;
  const [version, setVersion] = useState<number | null>(null);
  const [lockedCertificateKind, setLockedCertificateKind] = useState(false);
  const [loading, setLoading] = useState(editing);
  const [reloadToken, setReloadToken] = useState(0);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const title = `${editing ? 'Upraviť' : 'Pridať'} typ HR termínu`;

  const form = useForm({
    initialValues: {
      isGlobal: true,
      organizationIds: [] as string[],
      name: '',
      description: '',
      itemKind: 'ordinary',
      recurrenceQuantity: 1,
      recurrenceUnit: 'years',
      deviceTypeId: '',
      variants: [] as Array<{ variantId?: number; name: string; description: string }>,
    },
    validate: {
      name: (value) => (value.trim() ? null : 'Pole je povinné'),
      organizationIds: (value, values) =>
        !values.isGlobal && value.length === 0 ? 'Vyberte aspoň jednu organizáciu' : null,
      deviceTypeId: (value, values) =>
        values.itemKind === 'technician-certificate' && !value ? 'Vyberte typ zariadenia' : null,
      recurrenceQuantity: (value, values) =>
        values.itemKind === 'technician-certificate' && (values.recurrenceUnit === 'none' || value < 1)
          ? 'Zadajte opakovanie'
          : null,
      variants: {
        name: (value) => (value.trim() ? null : 'Pole je povinné'),
      },
    },
  });
  useDirtyFormHandler(form, true);

  useEffect(() => {
    form.reset();
    setLockedCertificateKind(false);
    if (!termTypeId) {
      setVersion(null);
      setLoading(false);
      setError(null);
      return;
    }

    setLoading(true);
    setError(null);
    setVersion(null);
    getAction('HrListTermTypes')({ query: { filters: { includeArchived: true } } })
      .then((termTypes) => {
        const termType = termTypes.find((candidate) => String(candidate.termTypeId) === termTypeId);
        if (!termType) {
          setError('Konfigurácia neexistuje alebo k nej nemáte prístup.');
          return;
        }
        setVersion(termType.version ?? null);
        setLockedCertificateKind(termType.kind === 'technician-certificate');
        const authoritativeValues = {
          isGlobal: termType.isGlobal,
          organizationIds: termType.organizations.map((organization) => String(organization.organizationId)),
          name: termType.name ?? '',
          description: termType.description ?? '',
          itemKind: termType.kind ?? 'ordinary',
          recurrenceQuantity:
            termType.defaultRecurrence?.quantity ?? (termType.kind === 'technician-certificate' ? 1 : 0),
          recurrenceUnit:
            termType.defaultRecurrence?.unit ?? (termType.kind === 'technician-certificate' ? 'years' : 'none'),
          deviceTypeId: termType.deviceTypeId ? String(termType.deviceTypeId) : '',
          variants: (termType.variants ?? []).map((variant) => ({
            variantId: variant.variantId,
            name: variant.name,
            description: variant.description,
          })),
        };
        form.setValues(authoritativeValues);
        form.resetDirty(authoritativeValues);
      })
      .catch(() => setError('Konfiguráciu sa nepodarilo načítať.'))
      .finally(() => setLoading(false));
  }, [getAction, reloadToken, termTypeId]);

  const submit = form.onSubmit(async (values) => {
    setSaving(true);
    setError(null);
    const payload = {
      isGlobal: values.isGlobal,
      organizationIds: values.isGlobal ? [] : values.organizationIds.map(Number),
      name: values.name.trim(),
      description: values.description.trim() || undefined,
      kind: values.itemKind,
      defaultRecurrence:
        values.recurrenceUnit !== 'none'
          ? { quantity: values.recurrenceQuantity, unit: values.recurrenceUnit }
          : undefined,
      deviceTypeId: values.itemKind === 'technician-certificate' ? Number(values.deviceTypeId) : undefined,
      variants: values.variants.map((variant) => ({
        ...(variant.variantId ? { variantId: variant.variantId } : {}),
        name: variant.name.trim(),
        description: variant.description.trim(),
      })),
    };

    try {
      if (editing) {
        await getAction('HrUpdateTermType')({
          parameters: { termTypeId: termTypeId! },
          payload: { ...payload, version: version! },
        });
      } else {
        await getAction('HrCreateTermType')({ payload });
      }
      notifications.show({
        title: 'Typ HR termínu bol uložený',
        message: 'Zmena je dostupná v zozname.',
        color: SUCCESS_NOTIFICATION_COLOR,
      });
      form.resetDirty(values);
      navigate(HR_TERM_TYPES_PAGE_PATH.original);
    } catch (caught) {
      setError('Typ HR termínu sa nepodarilo uložiť. Skontrolujte rozsah, jedinečnosť a povinné hodnoty.');
    } finally {
      setSaving(false);
    }
  });

  if (roleId !== ROLE_ADMIN_ID) return <Navigate to={DASHBOARD_PAGE_PATH.original} replace />;
  if (loading) {
    return (
      <DashboardLayout
        title={title}
        breadcrumbs={[{ title: 'Typy HR termínov', link: HR_TERM_TYPES_PAGE_PATH.original }, { title }]}
      >
        <Group position="center" py={80}>
          <Loader aria-label="Načítavam typ HR termínu" />
        </Group>
      </DashboardLayout>
    );
  }
  if (editing && version === null) {
    return (
      <DashboardLayout
        title={title}
        breadcrumbs={[{ title: 'Typy HR termínov', link: HR_TERM_TYPES_PAGE_PATH.original }, { title }]}
      >
        <Alert color="red" title="Typ HR termínu nie je možné upraviť">
          {error ?? 'Typ HR termínu sa nepodarilo načítať.'}
          <Button mt={12} variant="tertiary" onClick={() => setReloadToken((value) => value + 1)}>
            Načítať znova
          </Button>
        </Alert>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout
      title={title}
      breadcrumbs={[{ title: 'Typy HR termínov', link: HR_TERM_TYPES_PAGE_PATH.original }, { title }]}
    >
      {error && (
        <Alert color="red" mb={16}>
          {error}
        </Alert>
      )}
      <FormWrapper
        primaryButtonText={editing ? 'Uložiť' : 'Vytvoriť'}
        primaryIcon={editing ? <IconCheck stroke={1.5} /> : <IconPlus stroke={1.5} />}
        secondaryButtonText="Zrušiť"
        onSecondaryClick={() => navigate(HR_TERM_TYPES_PAGE_PATH.original)}
        skipSecondaryButtonConfirm={!form.isDirty()}
        onSubmit={submit}
        loading={saving}
      >
        <FormInputGroup groupTitle="Základné info">
          <SimpleGrid cols={2} spacing={isTablet ? 20 : 40}>
            <TextInput size="lg" label="Názov" withAsterisk {...form.getInputProps('name')} />
            <Textarea size="lg" label="Popis" {...form.getInputProps('description')} />
          </SimpleGrid>
        </FormInputGroup>
        <OrganizationStructureScope
          allOrganizations={form.values.isGlobal}
          selectedOrganizations={form.values.organizationIds}
          allOrganizationsText="Typ termínu je dostupný pre všetky organizácie."
          error={form.errors.organizationIds}
          name="organizationIds"
          showDiscarded
          onAllOrganizationsChange={(value) => form.setFieldValue('isGlobal', value)}
          onSelectedOrganizationsChange={(value) => form.setFieldValue('organizationIds', value)}
        />
        <FormInputGroup groupTitle="Nastavenia">
          <SimpleGrid cols={2} spacing={isTablet ? 20 : 40}>
            <Select
              size="lg"
              label="Druh"
              data={[
                { value: 'ordinary', label: 'Bežný termín' },
                { value: 'technician-certificate', label: 'Osvedčenie technika' },
              ]}
              disabled={lockedCertificateKind}
              {...form.getInputProps('itemKind')}
              onChange={(value) => {
                const itemKind = value ?? 'ordinary';
                form.setFieldValue('itemKind', itemKind);
                if (itemKind === 'technician-certificate' && form.values.recurrenceUnit === 'none') {
                  form.setFieldValue('recurrenceQuantity', 1);
                  form.setFieldValue('recurrenceUnit', 'years');
                }
              }}
            />
            {form.values.itemKind === 'technician-certificate' ? (
              <DeviceTypeSelect
                size="lg"
                label="Typ zariadenia"
                withAsterisk
                disabled={lockedCertificateKind}
                {...form.getInputProps('deviceTypeId')}
              />
            ) : (
              <span />
            )}
          </SimpleGrid>
          <SimpleGrid cols={2} spacing={isTablet ? 20 : 40}>
            <NumberInput
              size="lg"
              label="Opakovanie"
              min={1}
              disabled={form.values.itemKind === 'ordinary' && form.values.recurrenceUnit === 'none'}
              withAsterisk={form.values.itemKind === 'technician-certificate'}
              {...form.getInputProps('recurrenceQuantity')}
            />
            <Select
              size="lg"
              label="Jednotka opakovania"
              data={[
                ...(form.values.itemKind === 'ordinary' ? [{ value: 'none', label: 'Bez opakovania' }] : []),
                { value: 'days', label: 'Dni' },
                { value: 'weeks', label: 'Týždne' },
                { value: 'months', label: 'Mesiace' },
                { value: 'years', label: 'Roky' },
              ]}
              {...form.getInputProps('recurrenceUnit')}
              onChange={(value) => {
                const recurrenceUnit = value ?? 'none';
                form.setFieldValue('recurrenceUnit', recurrenceUnit);
                if (recurrenceUnit === 'none') {
                  form.setFieldValue('recurrenceQuantity', 0);
                } else if (form.values.recurrenceQuantity < 1) {
                  form.setFieldValue('recurrenceQuantity', 1);
                }
              }}
            />
          </SimpleGrid>
        </FormInputGroup>
        <FormInputGroup groupTitle="Varianty">
          <Stack spacing={16}>
            {form.values.variants.map((variant, index) => (
              <Group key={variant.variantId ?? index} align="flex-start" noWrap>
                <TextInput
                  size="lg"
                  label="Názov variantu"
                  withAsterisk
                  sx={{ flex: 1 }}
                  {...form.getInputProps(`variants.${index}.name`)}
                />
                <Textarea
                  size="lg"
                  label="Popis variantu"
                  autosize
                  minRows={1}
                  sx={{ flex: 2 }}
                  {...form.getInputProps(`variants.${index}.description`)}
                />
                <ActionIcon
                  mt={36}
                  size="lg"
                  color="red"
                  aria-label={`Odstrániť variant ${variant.name || index + 1}`}
                  onClick={() => form.removeListItem('variants', index)}
                >
                  <IconTrashX stroke={1.5} />
                </ActionIcon>
              </Group>
            ))}
            <Group position="left">
              <Button
                type="button"
                variant="subtle"
                leftIcon={<IconPlus stroke={1.5} />}
                onClick={() => form.insertListItem('variants', { name: '', description: '' })}
              >
                Pridať variant
              </Button>
            </Group>
          </Stack>
        </FormInputGroup>
      </FormWrapper>
    </DashboardLayout>
  );
}
